import React, { useRef, useState } from 'react';
import { useInfiniteQuery } from 'react-query';

import { SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { getCommunityReviews } from 'apis/ReviewsAPI';
import { Divider } from 'components/lib/Divider';
import Input from 'components/lib/Input';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import { usePublicationContext } from 'components/Publication/PublicationContext';
import { PAGINATION_PAGE_SIZE } from 'constants/pagination';
import useDataQuarterSelection from 'hooks/useDataQuarterSelection';
import { getQuarterEndDate } from 'utils/dateQuarter';
import { useDebounce } from 'utils/hooks/useDebounce';
import useInfiniteScroll from 'utils/hooks/useInfiniteScroll';

import { ReviewItem as ReviewItemType } from './types';
import ReviewItem from '../Reviews/ReviewItem';
import { useWidgetContext } from '../Widget/WidgetContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 374px;
  height: 100%;
`;

const Title = styled.div`
  font-size: 16px;
  font-family: var(--font-bold);
`;

const SearchInput = styled(Input)`
  width: 100%;
`;

const ReviewsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  flex: 1;
`;

const NoDataText = styled.div`
  text-align: center;
  color: var(--text-secondary);
  padding: 20px;
`;

const LoadMoreTrigger = styled.div`
  min-height: 1px;
`;

type Props = {
  selectedCommunityId: number;
};

export const AllReviews = ({ selectedCommunityId }: Props) => {
  const [searchQuery, setSearchQuery] = useState('');
  const reviewsListRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const { publication } = usePublicationContext();
  const { filters, isModalPreview } = useWidgetContext();
  const { selection, isLatestQuarterSelected, snapshotUuids } = useDataQuarterSelection();

  const { reviewSources, reviewCategories } = filters;
  const debouncedSearchQuery = useDebounce(searchQuery);

  const {
    data: reviewsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery(
    [
      'reviews',
      selectedCommunityId,
      reviewSources,
      reviewCategories,
      isLatestQuarterSelected,
      selection?.selectedQuarterSlug,
      debouncedSearchQuery,
    ],
    ({ pageParam = null }) =>
      getCommunityReviews(selectedCommunityId, {
        nextPageUrl: pageParam,
        review_sources: reviewSources?.length > 0 ? reviewSources : undefined,
        review_categories: reviewCategories?.length > 0 ? reviewCategories : undefined,
        ...(!isLatestQuarterSelected
          ? { end_created_at: getQuarterEndDate(selection?.selectedQuarterSlug ?? null) }
          : {}),
        search: debouncedSearchQuery?.trim() || undefined,
        ...(publication?.hash ? { public_hash: publication.hash } : {}),
        page_size: PAGINATION_PAGE_SIZE.MODERATE,
      }),
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: selectedCommunityId > 0 && snapshotUuids !== null,
      getNextPageParam: (lastPage) => lastPage.next || undefined,
    }
  );

  useInfiniteScroll({
    rootRef: reviewsListRef,
    triggerRef: loadMoreTriggerRef,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore: () => fetchNextPage(),
  });

  const reviews = reviewsData?.pages.flatMap((page) => page.results) ?? [];

  return (
    <Container>
      <Title>All Reviews</Title>
      <SearchInput
        placeholder="Search reviews"
        prefix={<SearchOutlined />}
        value={searchQuery}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
        allowClear
        data-testid="rs-widget-reviews-search-input"
      />
      <Divider />
      <ReviewsList ref={reviewsListRef} className={isModalPreview ? '' : 'mini-scroll'}>
        {isLoading && <LoaderRibbed size="small" />}
        {!isLoading &&
          reviews.length > 0 &&
          reviews.map((review: ReviewItemType, index: number) => (
            <ReviewItem key={review.id} item={review} index={index} noColors textFontSize="12px" />
          ))}
        {hasNextPage && <LoadMoreTrigger ref={loadMoreTriggerRef} />}
        {isFetchingNextPage && <LoaderRibbed size="small" />}
        {!isLoading && reviews.length === 0 && <NoDataText>No reviews found</NoDataText>}
      </ReviewsList>
    </Container>
  );
};

export default AllReviews;
